import resolver from "@congrs/resolver";
import { Button, Flex, Paper, Title, Modal, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { MultiSelect, Select, TextInput } from "react-hook-form-mantine";

import { usePatchEvent } from "../../../../api/event";
import { fullFormats } from "ajv-formats/dist/formats";
import { extractDirtyData } from "../../../../utils/dirtyData";
import LocalizedInput from "../../../common/LocalizedInput";
import { web_patchCustomEventSchema } from "@congrs/schemas";
import { useGetAllActiveLanguages } from "../../../../api/languages";
import { mergeI18nWithDefaults } from "../../../../utils/i18nUtils";
import AppAvailabilityBadge from "../../../AppAvailabilityBadge";
import { useLanguage } from "../../../../services/LanguageContext";

function General({ data, isSysAdmin }) {
  const mainLanguage = data?.language || "en";

  const [pendingSubmitData, setPendingSubmitData] = useState(null);

  const { data: languages } = useGetAllActiveLanguages();
  const { setLanguage } = useLanguage();

  const otherLanguages = data?.secondaryLanguages || [];

  const defaultValues = {
    nameI18n: mergeI18nWithDefaults(
      data?.nameI18n,
      mainLanguage,
      otherLanguages
    ),
    status: data?.status || "draft",
    mainLocation: data?.mainLocation || "",
    contactEmail: data?.contactEmail || "",
    language: data?.language || "en",
    secondaryLanguages:
      data?.secondaryLanguages?.filter((lang) =>
        languages?.data?.some((activeLang) => activeLang.code === lang)
      ) || [],
  };

  const { mutate, isPending: isLoading } = usePatchEvent(data?.id);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { isDirty, dirtyFields },
    reset,
  } = useForm({
    defaultValues,
    resolver: resolver(web_patchCustomEventSchema, {
      formats: fullFormats,
    }),
  });

  //when secondary language is set as "not active", reset form so that language will not be shown as preselected option in multiselect
  useEffect(() => {
    if (languages?.data && data) {
      const filteredLanguages =
        data.secondaryLanguages?.filter((lang) =>
          languages.data.some((activeLang) => activeLang.code === lang)
        ) || [];

      reset({
        nameI18n: mergeI18nWithDefaults(
          data.nameI18n,
          mainLanguage,
          filteredLanguages
        ),
        status: data.status || "draft",
        mainLocation: data.mainLocation || "",
        contactEmail: data.contactEmail || "",
        language: data.language || "en",
        secondaryLanguages: filteredLanguages,
      });
    }
  }, [languages?.data, data?.id, reset]);

  const onSubmit = (formData) => {
    const dirtyData = extractDirtyData(formData, dirtyFields);

    const isActivating =
      !isSysAdmin && data.status === "draft" && formData.status === "active";

    if (isActivating && data.autoCloseDays) {
      setPendingSubmitData({ formData, dirtyData });
      return;
    }

    mutate(dirtyData, {
      onSuccess: () => {
        reset(formData);
        syncContentLanguage(formData.language);
      },
    });
  };

  const handleConfirm = () => {
    const { formData, dirtyData } = pendingSubmitData;
    mutate(dirtyData, {
      onSuccess: () => {
        reset(formData);
        syncContentLanguage(formData.language);
        setPendingSubmitData(null);
      },
    });
  };

  // After a successful save, switch the global content language to the event's
  // (possibly new) main language so the navbar event name reflects it. Only
  // when it actually changed, to avoid overriding the current view otherwise.
  const syncContentLanguage = (savedLanguage) => {
    if (savedLanguage && savedLanguage !== data.language) {
      setLanguage(savedLanguage);
    }
  };

  const selectedLanguage = watch("language");
  const selectedSecondaryLanguages = watch("secondaryLanguages");

  //set secondary language when primary is selected
  const secondaryLanguageOptions = languages?.data
    ? languages?.data?.filter((language) => language.code !== selectedLanguage)
    : [];

  // When primary language changes, remove it from secondary languages if present
  useEffect(() => {
    const secondary = getValues("secondaryLanguages") ?? [];
    const filtered = secondary.filter((lang) => lang !== selectedLanguage);
    if (filtered.length < secondary.length) {
      setValue("secondaryLanguages", filtered, { shouldDirty: true });
    }
  }, [selectedLanguage, getValues, setValue]);

  // If user is not a system admin,
  // disable previous status, because they can
  // update status in order 'draft' -> 'active' -> 'closed'
  const statusOptions = [
    {
      label: "Draft",
      value: "draft",
      disabled: !isSysAdmin && data.status !== "draft",
    },
    {
      label: "Active",
      value: "active",
      disabled: !isSysAdmin && data.status === "closed",
    },
    { label: "Closed", value: "closed" },
  ];

  return (
    <Flex direction="column" flex="1" miw={{ lg: 500, sm: 300 }}>
      <Title order={2} mt="sm" mb="xs">
        General settings
      </Title>
      <Paper withBorder shadow="md" p={16} radius="md">
        <form onSubmit={handleSubmit(onSubmit)}>
          <LocalizedInput
            control={control}
            fields={[
              {
                nameBase: "nameI18n",
                label: "Event title",
                component: TextInput,
                props: {
                  withAsterisk: true,
                  placeholder: "Enter the event title",
                  mt: "md",
                },
              },
            ]}
            mainLanguage={selectedLanguage}
            otherLanguages={selectedSecondaryLanguages}
          />

          <Select
            label="Main Event language"
            placeholder="Select language"
            name="language"
            data={
              languages?.data?.map((language) => {
                return { value: language.code, label: language.name };
              }) || []
            }
            control={control}
            mt="md"
            allowDeselect={false}
          />

          {data?.isEnabledMultilingual && (
            <MultiSelect
              label="Secondary languages"
              name="secondaryLanguages"
              placeholder="Select secondary languages"
              data={secondaryLanguageOptions?.map((language) => {
                return {
                  value: language.code,
                  label: `${language.name} (${language.code.toUpperCase()})`,
                };
              })}
              control={control}
              mt="md"
            />
          )}

          <Flex align="flex-end" gap="md" mt="md">
            <Select
              label="Status"
              placeholder="Select status"
              name="status"
              data={statusOptions}
              control={control}
              allowDeselect={false}
              style={{ flex: 1 }}
            />
            <AppAvailabilityBadge
              activatedAt={data.status === "draft" ? null : data?.activatedAt}
              autoCloseDays={data?.autoCloseDays}
            />
          </Flex>

          <TextInput
            label="Main location"
            type="text"
            name="mainLocation"
            defaultValue={data?.mainLocation}
            control={control}
            mt="md"
            placeholder="Enter the main location of the event (e.g. Sava Centar)"
          />

          <TextInput
            label="Contact email"
            type="text"
            name="contactEmail"
            control={control}
            mt="md"
            placeholder="Enter the contact email for the event"
          />

          <Button type="submit" mt="md" loading={isLoading} disabled={!isDirty}>
            Save
          </Button>
        </form>
      </Paper>

      <Modal
        opened={!!pendingSubmitData}
        onClose={() => setPendingSubmitData(null)}
        title="Set event as active?"
      >
        <Text mb="md">
          You are about to set the event as active. This will enable users to
          see the event on the app and the event will stay active for{" "}
          <strong>{data?.autoCloseDays} days</strong>. Are you sure?
        </Text>
        <Flex justify="flex-end" gap="sm">
          <Button variant="default" onClick={() => setPendingSubmitData(null)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} loading={isLoading}>
            Confirm
          </Button>
        </Flex>
      </Modal>
    </Flex>
  );
}

export default General;
